import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Container,
  Card,
  CardContent,
  Button,
  IconButton,
  TextField,
  Snackbar,
  Alert,
  Grid,
  Paper,
  Fade,
  Zoom,
  Slide,
  InputAdornment,
  Divider,
  Stack,
  Fab,
  Tooltip,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
} from "@mui/material";
import {
  ArrowBack,
  Person,
  Business,
  Phone,
  Email,
  Language,
  LocationOn,
  Save,
  Share,
  Edit,
  Add,
  Delete,
  Work,
  BusinessCenter,
  Notes,
  CorporateFare,
  Folder,
  LocalOffer,
  Group,
  Tag,
  Warning,
  Close,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useTheme } from "../contexts/ThemeContext";
import makeAPIRequest from "../global/apiCall";
import { POST, ADD_CONTACT } from "../global/apiKeys";
import GroupsManager from "../components/modern/GroupsManager";
import TagsManager from "../components/modern/TagsManager";

// Helper function to convert relative URLs to full URLs
const getFullImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url; // Already a full URL
  if (url.startsWith('data:')) return url; // Base64 data URL
  // Convert relative URL to full URL
  const BASE_URL = "http://localhost:3006/";
  return `${BASE_URL}${url.startsWith('/') ? url.slice(1) : url}`;
};

const Information = () => {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  
  const [fields, setFields] = useState({
    fullName: "",
    orgName: "",
    designation: "",
    phones: [{ phone: "" }],
    emails: [{ email: "" }],
    website: "",
    address: "",
    imgUrl: "",
    groupId: "",
    selectedTags: [],
    notes: "",
    priority: 0,
  });
  
  const [originalData, setOriginalData] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });
  const [loading, setLoading] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [tempValue, setTempValue] = useState("");
  const [groupsDialogOpen, setGroupsDialogOpen] = useState(false);
  const [tagsDialogOpen, setTagsDialogOpen] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [selectedTagsList, setSelectedTagsList] = useState([]);
  const [duplicateContacts, setDuplicateContacts] = useState([]);
  const [showDuplicateAlert, setShowDuplicateAlert] = useState(false);
  const [cardImages, setCardImages] = useState({ front: null, back: null });
  const [currentImageView, setCurrentImageView] = useState('front');
  const [hasBothSides, setHasBothSides] = useState(false);

  // Load extracted data on component mount and map to fields structure
  useEffect(() => {
    try {
      const uploadedData = localStorage.getItem('uploadedFileData');
      if (uploadedData) {
        const data = JSON.parse(uploadedData);
        console.log('📋 Raw API Response:', data);

        // Handle both single-side and dual-side card data
        let apiData = null;
        let imageUrls = { front: null, back: null };
        let bothSides = false;

        // Check if this is multi-side data (has combinedData, frontData, backData)
        if (data.combinedData || data.frontData || data.backData) {
          // Multi-side card data
          apiData = data.combinedData || data.frontData || data.backData;
          console.log('🔍 Checking image URLs:');
          console.log('🔍 data.frontImageUrl:', data.frontImageUrl);
          console.log('🔍 data.backImageUrl:', data.backImageUrl);
          console.log('🔍 data.cardImages:', data.cardImages);
          console.log('🔍 data.frontImage:', data.frontImage);
          console.log('🔍 data.backImage:', data.backImage);

          // Try multiple sources for image URLs and convert to full URLs
          imageUrls.front = getFullImageUrl(data.frontImageUrl || data.cardImages?.front || data.frontImage);
          imageUrls.back = getFullImageUrl(data.backImageUrl || data.cardImages?.back || data.backImage);
          bothSides = !!(imageUrls.front && imageUrls.back);
        } else if (data.data) {
          // Single-side card data (legacy)
          apiData = data.data;
          imageUrls.front = getFullImageUrl(data.fileUrl || data.frontImage || "");
          imageUrls.back = getFullImageUrl(data.backImage);
          bothSides = !!(imageUrls.front && imageUrls.back);
        }

        if (apiData) {
          // Handle different email formats from API
          let emailList = [];
          if (Array.isArray(apiData.email)) {
            emailList = apiData.email;
          } else if (apiData.email) {
            emailList = [apiData.email];
          }

          const mappedFields = {
            fullName: apiData.person_name || "",
            orgName: apiData.company_name || "",
            designation: apiData.person_designation || "",
            phones: apiData.phone_number && apiData.phone_number.length > 0
              ? apiData.phone_number.map(phone => ({ phone: phone }))
              : [{ phone: "" }],
            emails: emailList.length > 0
              ? emailList.map(email => ({ email: email }))
              : [{ email: "" }],
            website: apiData.website || "",
            address: apiData.address || "",
            imgUrl: imageUrls.front || "", // Keep for backward compatibility
            groupId: "",
            selectedTags: [],
            notes: "",
            priority: 0,
          };

          console.log('📋 Mapped fields:', mappedFields);
          console.log('🖼️ Card images:', imageUrls);
          console.log('📱 Has both sides:', bothSides);

          setFields(mappedFields);
          setOriginalData(data);
          setCardImages(imageUrls);
          setHasBothSides(bothSides);
          setCurrentImageView(imageUrls.front ? 'front' : 'back');

          // Check for duplicate phone numbers
          if (data.hasDuplicates && data.duplicatePhoneContacts && data.duplicatePhoneContacts.length > 0) {
            console.log('🚨 Duplicate phone numbers detected:', data.duplicatePhoneContacts);
            setDuplicateContacts(data.duplicatePhoneContacts);
            setShowDuplicateAlert(true);
            showSnackbar(
              `⚠️ Found ${data.duplicatePhoneContacts.length} existing contact(s) with similar phone number(s)`,
              'warning'
            );
          }
        }
      } else {
        showSnackbar('No business card data found', 'warning');
        navigate('/create');
      }
    } catch (error) {
      console.error('Error loading data:', error);
      showSnackbar('Error loading business card data', 'error');
      navigate('/create');
    }
  }, [navigate]);

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const handleFieldChange = (key, value) => {
    setFields(prev => ({ ...prev, [key]: value }));
  };

  const handlePhoneChange = (index, value) => {
    const updatedPhones = [...fields.phones];
    updatedPhones[index] = { phone: value };
    setFields(prev => ({ ...prev, phones: updatedPhones }));
  };

  const handleEmailChange = (index, value) => {
    const updatedEmails = [...fields.emails];
    updatedEmails[index] = { email: value };
    setFields(prev => ({ ...prev, emails: updatedEmails }));
  };

  const handleAddPhone = () => {
    setFields(prev => ({
      ...prev,
      phones: [...prev.phones, { phone: "" }],
    }));
  };

  const handleRemovePhone = (index) => {
    if (fields.phones.length > 1) {
      setFields(prev => ({
        ...prev,
        phones: prev.phones.filter((_, i) => i !== index),
      }));
    }
  };

  const handleAddEmail = () => {
    setFields(prev => ({
      ...prev,
      emails: [...prev.emails, { email: "" }],
    }));
  };

  const handleRemoveEmail = (index) => {
    if (fields.emails.length > 1) {
      setFields(prev => ({
        ...prev,
        emails: prev.emails.filter((_, i) => i !== index),
      }));
    }
  };

  const handleSaveContact = async () => {
    if (!fields.fullName && !fields.phones[0]?.phone && !fields.emails[0]?.email) {
      showSnackbar('Please provide at least a name, phone, or email address', 'warning');
      return;
    }
    
    setLoading(true);
    try {
      // Prepare contact data matching the expected structure
      const contactData = {
        ...fields,
        // Clean up phone and email arrays to remove empty entries
        phones: fields.phones.filter(p => p.phone && p.phone.trim() !== ''),
        emails: fields.emails.filter(e => e.email && e.email.trim() !== ''),
        // Handle UUID fields - convert empty strings to null
        groupId: fields.groupId && fields.groupId.trim() !== '' ? fields.groupId : null,
        // Handle image URL - convert empty string to null, exclude base64 data
        imgUrl: fields.imgUrl && fields.imgUrl.trim() !== '' && !fields.imgUrl.startsWith('data:') ? fields.imgUrl : null,
        // Ensure tagIds is an array, not empty string
        tagIds: Array.isArray(fields.selectedTags) ? fields.selectedTags : [],
        // Remove selectedTags field as it's mapped to tagIds
        selectedTags: undefined,
        // Add metadata including card images and scan type
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        source: 'business_card_scan',
        extractedData: originalData,
        // Ensure only URLs are sent, not base64 data
        cardImages: {
          front: cardImages.front && cardImages.front.trim() !== '' && !cardImages.front.startsWith('data:') ? cardImages.front : null,
          back: cardImages.back && cardImages.back.trim() !== '' && !cardImages.back.startsWith('data:') ? cardImages.back : null
        },
        hasBothSides: hasBothSides,
        scanType: hasBothSides ? 'dual' : 'single'
      };

      // Ensure at least one phone or email entry
      if (contactData.phones.length === 0) {
        contactData.phones = [{ phone: "" }];
      }
      if (contactData.emails.length === 0) {
        contactData.emails = [{ email: "" }];
      }

      console.log('💾 Debug cardImages state before save:');
      console.log('💾 cardImages.front:', cardImages.front);
      console.log('💾 cardImages.back:', cardImages.back);
      console.log('💾 hasBothSides:', hasBothSides);
      console.log('💾 Final cardImages being sent:', contactData.cardImages);
      console.log('💾 Saving contact data:', contactData);
      const response = await makeAPIRequest(POST, ADD_CONTACT, contactData);
      console.log('💾 Contact saved successfully:', response);
      
      showSnackbar('Contact saved successfully!', 'success');
      
      // Update localStorage with the saved data
      localStorage.setItem('contactData', JSON.stringify(contactData));
      
      // Clear uploaded data
      localStorage.removeItem('uploadedFileData');
      
      // Optionally navigate back to dashboard or show success state
      setTimeout(() => {
        navigate('/dashboard');
      }, 2000);
      
    } catch (error) {
      console.error('💾 Save error:', error);
      showSnackbar(
        error.message || 'Failed to save contact. Please try again.', 
        'error'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleShare = async () => {
    const shareText = `${fields.fullName}\n${fields.designation ? `${fields.designation}\n` : ''}${fields.orgName ? `${fields.orgName}\n` : ''}${fields.phones[0]?.phone || ''}\n${fields.emails[0]?.email || ''}`;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${fields.fullName} - Contact Info`,
          text: shareText,
        });
      } catch (error) {
        console.log('Share cancelled or failed');
      }
    } else {
      navigator.clipboard.writeText(shareText);
      showSnackbar('Contact info copied to clipboard!', 'success');
    }
  };

  // Edit functionality
  const handleEditField = (field, currentValue) => {
    setEditingField(field);
    setTempValue(currentValue || "");
  };

  const handleSaveField = () => {
    if (editingField) {
      if (editingField.startsWith('phone-')) {
        const index = parseInt(editingField.split('-')[1]);
        handlePhoneChange(index, tempValue);
      } else if (editingField.startsWith('email-')) {
        const index = parseInt(editingField.split('-')[1]);
        handleEmailChange(index, tempValue);
      } else {
        handleFieldChange(editingField, tempValue);
      }
      setEditingField(null);
      setTempValue("");
    }
  };

  const handleCancelEdit = () => {
    setEditingField(null);
    setTempValue("");
  };

  const handleGroupSelect = (group) => {
    setSelectedGroup(group);
    setFields(prev => ({ ...prev, groupId: group?.id || "" }));
  };

  const handleTagSelect = (tags) => {
    setSelectedTagsList(tags);
    setFields(prev => ({ ...prev, selectedTags: tags.map(tag => tag.id) }));
  };

  if (!fields.fullName && !originalData) {
    return (
      <Container maxWidth="md" sx={{ py: 4, textAlign: 'center' }}>
        <Typography variant="h6" color="text.secondary">
          Loading business card information...
        </Typography>
      </Container>
    );
  }

  return (
    <Container
      maxWidth={false}
      sx={{
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        background: isDark 
          ? 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%)'
          : 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 50%, #cbd5e1 100%)',
        position: "relative",
        p: 0,
      }}
    >
      {/* Header */}
      <Fade in timeout={600}>
        <Paper
          elevation={0}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            p: 2,
            background: isDark 
              ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.95) 0%, rgba(15, 23, 42, 0.9) 100%)'
              : 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.9) 100%)',
            backdropFilter: 'blur(20px)',
            borderBottom: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
            position: 'sticky',
            top: 0,
            zIndex: 10,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton
              onClick={() => navigate(-1)}
              sx={{ 
                color: isDark ? 'white' : 'primary.main', 
                mr: 2,
                background: isDark 
                  ? 'linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(37, 99, 235, 0.1) 100%)'
                  : 'linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(37, 99, 235, 0.1) 100%)',
                '&:hover': {
                  background: isDark 
                    ? 'linear-gradient(135deg, rgba(59, 130, 246, 0.2) 0%, rgba(37, 99, 235, 0.2) 100%)'
                    : 'linear-gradient(135deg, rgba(59, 130, 246, 0.2) 0%, rgba(37, 99, 235, 0.2) 100%)',
                  transform: 'scale(1.05)',
                },
                transition: 'all 0.2s ease',
              }}
            >
              <ArrowBack />
            </IconButton>
            <Typography 
              variant="h5" 
              sx={{ 
                fontWeight: 700,
                background: isDark 
                  ? 'linear-gradient(135deg, #ffffff 0%, #cbd5e1 100%)'
                  : 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                textShadow: isDark ? 'none' : '0 1px 2px rgba(0,0,0,0.1)',
              }}
            >
              Business Card Details
            </Typography>
          </Box>
        </Paper>
      </Fade>

      {/* Main Content */}
      <Box
        sx={{
          flex: 1,
          overflow: "auto",
          pb: 12,
          px: 2,
          pt: 3,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        {/* Business Card Image Preview */}
        {(cardImages.front || cardImages.back) && (
          <Zoom in timeout={700}>
            <Paper
              elevation={0}
              sx={{
                width: "100%",
                maxWidth: 400,
                mb: 2,
                p: 2,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                background: isDark
                  ? 'linear-gradient(135deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.04) 100%)'
                  : 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.85) 100%)',
                backdropFilter: 'blur(20px)',
                border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'}`,
                borderRadius: 4,
              }}
            >
              {/* Header with side selector */}
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
                mb: 2
              }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: isDark ? 'rgba(203, 213, 225, 0.8)' : 'text.secondary',
                    fontWeight: 500,
                  }}
                >
                  Scanned Business Card
                </Typography>

                {hasBothSides && (
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      size="small"
                      variant={currentImageView === 'front' ? 'contained' : 'outlined'}
                      onClick={() => setCurrentImageView('front')}
                      disabled={!cardImages.front}
                      sx={{
                        minWidth: 60,
                        fontSize: '0.75rem',
                        py: 0.5,
                        background: currentImageView === 'front'
                          ? 'linear-gradient(135deg, #00ff88, #00cc66)'
                          : 'transparent',
                        borderColor: currentImageView === 'front' ? '#00ff88' : 'rgba(255, 255, 255, 0.3)',
                        color: currentImageView === 'front' ? 'white' : (isDark ? 'white' : 'primary.main'),
                        '&:hover': {
                          background: currentImageView === 'front'
                            ? 'linear-gradient(135deg, #00cc66, #00aa55)'
                            : 'rgba(0, 255, 136, 0.1)',
                        }
                      }}
                    >
                      Front
                    </Button>
                    <Button
                      size="small"
                      variant={currentImageView === 'back' ? 'contained' : 'outlined'}
                      onClick={() => setCurrentImageView('back')}
                      disabled={!cardImages.back}
                      sx={{
                        minWidth: 60,
                        fontSize: '0.75rem',
                        py: 0.5,
                        background: currentImageView === 'back'
                          ? 'linear-gradient(135deg, #ffaa00, #ff8800)'
                          : 'transparent',
                        borderColor: currentImageView === 'back' ? '#ffaa00' : 'rgba(255, 255, 255, 0.3)',
                        color: currentImageView === 'back' ? 'white' : (isDark ? 'white' : 'primary.main'),
                        '&:hover': {
                          background: currentImageView === 'back'
                            ? 'linear-gradient(135deg, #ff8800, #ff6600)'
                            : 'rgba(255, 170, 0, 0.1)',
                        }
                      }}
                    >
                      Back
                    </Button>
                  </Box>
                )}
              </Box>

              {/* Image container */}
              <Box
                sx={{
                  width: '100%',
                  aspectRatio: '1.586/1', // Standard business card ratio
                  borderRadius: '8px',
                  overflow: 'hidden',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  border: `2px solid ${
                    currentImageView === 'front'
                      ? '#00ff88'
                      : currentImageView === 'back'
                        ? '#ffaa00'
                        : (isDark ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.1)')
                  }`,
                  position: 'relative',
                  transition: 'all 0.3s ease',
                }}
              >
                <img
                  src={cardImages[currentImageView] || cardImages.front || cardImages.back}
                  alt={`Business card ${currentImageView} side`}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    transition: 'all 0.3s ease',
                  }}
                  onError={(e) => {
                    console.error('Failed to load business card image:', e);
                    e.target.style.display = 'none';
                  }}
                />

                {/* Side indicator overlay */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    background: currentImageView === 'front'
                      ? 'linear-gradient(135deg, #00ff88, #00cc66)'
                      : 'linear-gradient(135deg, #ffaa00, #ff8800)',
                    color: 'white',
                    px: 1.5,
                    py: 0.5,
                    borderRadius: '12px',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    textTransform: 'capitalize',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                  }}
                >
                  {currentImageView}
                </Box>
              </Box>

              {/* Both sides indicator */}
              {hasBothSides && (
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  mt: 1.5,
                  px: 2,
                  py: 0.5,
                  borderRadius: '12px',
                  background: 'rgba(0, 255, 136, 0.1)',
                  border: '1px solid rgba(0, 255, 136, 0.3)',
                }}>
                  <Box sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    background: '#00ff88'
                  }} />
                  <Typography
                    variant="caption"
                    sx={{
                      color: '#00ff88',
                      fontWeight: 600,
                      fontSize: '0.75rem'
                    }}
                  >
                    Both sides captured
                  </Typography>
                </Box>
              )}
            </Paper>
          </Zoom>
        )}

        {/* Contact Header Card */}
        <Zoom in timeout={800}>
          <Paper
            elevation={0}
            sx={{ 
              width: "100%", 
              maxWidth: 400,
              mb: 3,
              p: 3,
              display: "flex", 
              flexDirection: "column",
              alignItems: "center",
              background: isDark 
                ? 'linear-gradient(135deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.04) 100%)'
                : 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.85) 100%)',
              backdropFilter: 'blur(20px)',
              border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'}`,
              borderRadius: 4,
            }}
          >
            {/* Name and Info */}
            <Typography 
              variant="h4" 
              sx={{ 
                fontWeight: 700, 
                mb: 1,
                textAlign: 'center',
                background: isDark 
                  ? 'linear-gradient(135deg, #ffffff 0%, #cbd5e1 100%)'
                  : 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              {fields.fullName || "Contact Name"}
            </Typography>
            
            {fields.designation && (
              <Typography 
                variant="h6" 
                sx={{ 
                  color: isDark ? 'rgba(203, 213, 225, 0.8)' : 'text.secondary',
                  mb: 1,
                  textAlign: 'center',
                  fontWeight: 500,
                }}
              >
                {fields.designation}
              </Typography>
            )}
            
            {fields.orgName && (
              <Typography 
                variant="body1" 
                sx={{ 
                  color: isDark ? 'rgba(203, 213, 225, 0.7)' : 'text.secondary',
                  textAlign: 'center',
                  fontWeight: 400,
                }}
              >
                {fields.orgName}
              </Typography>
            )}
          </Paper>
        </Zoom>

        {/* Contact Info Cards */}
        <Stack spacing={2} sx={{ width: "100%", maxWidth: 500 }}>
          {/* Full Name Card */}
          <Slide in timeout={600} direction="up">
            <Paper
              elevation={0}
              sx={{
                display: 'flex',
                alignItems: 'center',
                p: 2.5,
                background: isDark 
                  ? 'linear-gradient(135deg, rgba(255, 255, 255, 0.06) 0%, rgba(255, 255, 255, 0.03) 100%)'
                  : 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(255, 255, 255, 0.7) 100%)',
                backdropFilter: 'blur(10px)',
                border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)'}`,
                borderRadius: 3,
                boxShadow: isDark 
                  ? '0 10px 15px -3px rgba(0, 0, 0, 0.3), 0 4px 6px -2px rgba(0, 0, 0, 0.2)'
                  : '0 10px 15px -3px rgba(0, 0, 0, 0.08), 0 4px 6px -2px rgba(0, 0, 0, 0.04)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: isDark 
                    ? '0 20px 25px -5px rgba(0, 0, 0, 0.4), 0 10px 10px -5px rgba(0, 0, 0, 0.3)'
                    : '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0, flex: 1 }}>
                <Person sx={{ color: 'primary.main', mr: 2, flexShrink: 0 }} />
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  {editingField === 'fullName' ? (
                    <TextField
                      fullWidth
                      value={tempValue}
                      onChange={(e) => setTempValue(e.target.value)}
                      variant="standard"
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          handleSaveField();
                        }
                        if (e.key === 'Escape') {
                          e.preventDefault();
                          handleCancelEdit();
                        }
                      }}
                    />
                  ) : (
                    <>
                      <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
                        Full Name
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600, color: isDark ? 'white' : 'text.primary' }}>
                        {fields.fullName || "Not provided"}
                      </Typography>
                    </>
                  )}
                </Box>
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                {editingField === 'fullName' ? (
                  <>
                    <IconButton size="small" onClick={handleSaveField} color="primary">
                      <Save fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={handleCancelEdit}>
                      <Delete fontSize="small" />
                    </IconButton>
                  </>
                ) : (
                  <IconButton
                    size="small"
                    onClick={() => handleEditField('fullName', fields.fullName)}
                    sx={{ color: 'primary.main' }}
                  >
                    <Edit fontSize="small" />
                  </IconButton>
                )}
              </Box>
            </Paper>
          </Slide>

          {/* Designation Card */}
          <Slide in timeout={700} direction="up">
            <Paper
              elevation={0}
              sx={{
                display: 'flex',
                alignItems: 'center',
                p: 2.5,
                background: isDark 
                  ? 'linear-gradient(135deg, rgba(255, 255, 255, 0.06) 0%, rgba(255, 255, 255, 0.03) 100%)'
                  : 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(255, 255, 255, 0.7) 100%)',
                backdropFilter: 'blur(10px)',
                border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)'}`,
                borderRadius: 3,
                boxShadow: isDark 
                  ? '0 10px 15px -3px rgba(0, 0, 0, 0.3), 0 4px 6px -2px rgba(0, 0, 0, 0.2)'
                  : '0 10px 15px -3px rgba(0, 0, 0, 0.08), 0 4px 6px -2px rgba(0, 0, 0, 0.04)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: isDark 
                    ? '0 20px 25px -5px rgba(0, 0, 0, 0.4), 0 10px 10px -5px rgba(0, 0, 0, 0.3)'
                    : '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0, flex: 1 }}>
                <BusinessCenter sx={{ color: 'primary.main', mr: 2, flexShrink: 0 }} />
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  {editingField === 'designation' ? (
                    <TextField
                      fullWidth
                      value={tempValue}
                      onChange={(e) => setTempValue(e.target.value)}
                      variant="standard"
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          handleSaveField();
                        }
                        if (e.key === 'Escape') {
                          e.preventDefault();
                          handleCancelEdit();
                        }
                      }}
                    />
                  ) : (
                    <>
                      <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
                        Designation
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600, color: isDark ? 'white' : 'text.primary' }}>
                        {fields.designation || "Not provided"}
                      </Typography>
                    </>
                  )}
                </Box>
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                {editingField === 'designation' ? (
                  <>
                    <IconButton size="small" onClick={handleSaveField} color="primary">
                      <Save fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={handleCancelEdit}>
                      <Delete fontSize="small" />
                    </IconButton>
                  </>
                ) : (
                  <IconButton
                    size="small"
                    onClick={() => handleEditField('designation', fields.designation)}
                    sx={{ color: 'primary.main' }}
                  >
                    <Edit fontSize="small" />
                  </IconButton>
                )}
              </Box>
            </Paper>
          </Slide>

          {/* Company Card */}
          <Slide in timeout={800} direction="up">
            <Paper
              elevation={0}
              sx={{
                display: 'flex',
                alignItems: 'center',
                p: 2.5,
                background: isDark 
                  ? 'linear-gradient(135deg, rgba(255, 255, 255, 0.06) 0%, rgba(255, 255, 255, 0.03) 100%)'
                  : 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(255, 255, 255, 0.7) 100%)',
                backdropFilter: 'blur(10px)',
                border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)'}`,
                borderRadius: 3,
                boxShadow: isDark 
                  ? '0 10px 15px -3px rgba(0, 0, 0, 0.3), 0 4px 6px -2px rgba(0, 0, 0, 0.2)'
                  : '0 10px 15px -3px rgba(0, 0, 0, 0.08), 0 4px 6px -2px rgba(0, 0, 0, 0.04)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: isDark 
                    ? '0 20px 25px -5px rgba(0, 0, 0, 0.4), 0 10px 10px -5px rgba(0, 0, 0, 0.3)'
                    : '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0, flex: 1 }}>
                <CorporateFare sx={{ color: 'primary.main', mr: 2, flexShrink: 0 }} />
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  {editingField === 'orgName' ? (
                    <TextField
                      fullWidth
                      value={tempValue}
                      onChange={(e) => setTempValue(e.target.value)}
                      variant="standard"
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          handleSaveField();
                        }
                        if (e.key === 'Escape') {
                          e.preventDefault();
                          handleCancelEdit();
                        }
                      }}
                    />
                  ) : (
                    <>
                      <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
                        Company
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600, color: isDark ? 'white' : 'text.primary' }}>
                        {fields.orgName || "Not provided"}
                      </Typography>
                    </>
                  )}
                </Box>
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                {editingField === 'orgName' ? (
                  <>
                    <IconButton size="small" onClick={handleSaveField} color="primary">
                      <Save fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={handleCancelEdit}>
                      <Delete fontSize="small" />
                    </IconButton>
                  </>
                ) : (
                  <IconButton
                    size="small"
                    onClick={() => handleEditField('orgName', fields.orgName)}
                    sx={{ color: 'primary.main' }}
                  >
                    <Edit fontSize="small" />
                  </IconButton>
                )}
              </Box>
            </Paper>
          </Slide>

          {/* Phone Number Cards */}
          {fields.phones.map((phone, index) => (
            <Slide key={`phone-${index}`} in timeout={900 + index * 100} direction="up">
              <Paper
                elevation={0}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  p: 2.5,
                  background: isDark 
                    ? 'linear-gradient(135deg, rgba(255, 255, 255, 0.06) 0%, rgba(255, 255, 255, 0.03) 100%)'
                    : 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(255, 255, 255, 0.7) 100%)',
                  backdropFilter: 'blur(10px)',
                  border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)'}`,
                  borderRadius: 3,
                  boxShadow: isDark 
                    ? '0 10px 15px -3px rgba(0, 0, 0, 0.3), 0 4px 6px -2px rgba(0, 0, 0, 0.2)'
                    : '0 10px 15px -3px rgba(0, 0, 0, 0.08), 0 4px 6px -2px rgba(0, 0, 0, 0.04)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: isDark 
                      ? '0 20px 25px -5px rgba(0, 0, 0, 0.4), 0 10px 10px -5px rgba(0, 0, 0, 0.3)'
                      : '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                  },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0, flex: 1 }}>
                  <Phone sx={{ color: 'primary.main', mr: 2, flexShrink: 0 }} />
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    {editingField === `phone-${index}` ? (
                      <TextField
                        fullWidth
                        value={tempValue}
                        onChange={(e) => setTempValue(e.target.value)}
                        variant="standard"
                        autoFocus
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') handleSaveField();
                          if (e.key === 'Escape') handleCancelEdit();
                        }}
                      />
                    ) : (
                      <>
                        <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
                          Phone {index + 1}
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600, color: isDark ? 'white' : 'text.primary' }}>
                          {phone.phone || "Not provided"}
                        </Typography>
                      </>
                    )}
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {editingField === `phone-${index}` ? (
                    <>
                      <IconButton size="small" onClick={handleSaveField} color="primary">
                        <Save fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={handleCancelEdit}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </>
                  ) : (
                    <>
                      <IconButton
                        size="small"
                        onClick={() => handleEditField(`phone-${index}`, phone.phone)}
                        sx={{ color: 'primary.main' }}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                      {fields.phones.length > 1 && (
                        <IconButton
                          size="small"
                          onClick={() => handleRemovePhone(index)}
                          sx={{ color: 'error.main' }}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      )}
                      <IconButton
                        size="small"
                        onClick={handleAddPhone}
                        sx={{ color: 'success.main' }}
                      >
                        <Add fontSize="small" />
                      </IconButton>
                    </>
                  )}
                </Box>
              </Paper>
            </Slide>
          ))}

          {/* Email Cards */}
          {fields.emails.map((emailObj, index) => (
            <Slide key={`email-${index}`} in timeout={1000 + index * 100} direction="up">
              <Paper
                elevation={0}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  p: 2.5,
                  background: isDark 
                    ? 'linear-gradient(135deg, rgba(255, 255, 255, 0.06) 0%, rgba(255, 255, 255, 0.03) 100%)'
                    : 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(255, 255, 255, 0.7) 100%)',
                  backdropFilter: 'blur(10px)',
                  border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)'}`,
                  borderRadius: 3,
                  boxShadow: isDark 
                    ? '0 10px 15px -3px rgba(0, 0, 0, 0.3), 0 4px 6px -2px rgba(0, 0, 0, 0.2)'
                    : '0 10px 15px -3px rgba(0, 0, 0, 0.08), 0 4px 6px -2px rgba(0, 0, 0, 0.04)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: isDark 
                      ? '0 20px 25px -5px rgba(0, 0, 0, 0.4), 0 10px 10px -5px rgba(0, 0, 0, 0.3)'
                      : '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                  },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0, flex: 1 }}>
                  <Email sx={{ color: 'primary.main', mr: 2, flexShrink: 0 }} />
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    {editingField === `email-${index}` ? (
                      <TextField
                        fullWidth
                        value={tempValue}
                        onChange={(e) => setTempValue(e.target.value)}
                        variant="standard"
                        autoFocus
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') handleSaveField();
                          if (e.key === 'Escape') handleCancelEdit();
                        }}
                      />
                    ) : (
                      <>
                        <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
                          Email {index + 1}
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600, color: isDark ? 'white' : 'text.primary' }}>
                          {emailObj.email || "Not provided"}
                        </Typography>
                      </>
                    )}
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {editingField === `email-${index}` ? (
                    <>
                      <IconButton size="small" onClick={handleSaveField} color="primary">
                        <Save fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={handleCancelEdit}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </>
                  ) : (
                    <>
                      <IconButton
                        size="small"
                        onClick={() => handleEditField(`email-${index}`, emailObj.email)}
                        sx={{ color: 'primary.main' }}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                      {fields.emails.length > 1 && (
                        <IconButton
                          size="small"
                          onClick={() => handleRemoveEmail(index)}
                          sx={{ color: 'error.main' }}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      )}
                      <IconButton
                        size="small"
                        onClick={handleAddEmail}
                        sx={{ color: 'success.main' }}
                      >
                        <Add fontSize="small" />
                      </IconButton>
                    </>
                  )}
                </Box>
              </Paper>
            </Slide>
          ))}

          {/* Website Card */}
          <Slide in timeout={1100} direction="up">
            <Paper
              elevation={0}
              sx={{
                display: 'flex',
                alignItems: 'center',
                p: 2.5,
                background: isDark 
                  ? 'linear-gradient(135deg, rgba(255, 255, 255, 0.06) 0%, rgba(255, 255, 255, 0.03) 100%)'
                  : 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(255, 255, 255, 0.7) 100%)',
                backdropFilter: 'blur(10px)',
                border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)'}`,
                borderRadius: 3,
                boxShadow: isDark 
                  ? '0 10px 15px -3px rgba(0, 0, 0, 0.3), 0 4px 6px -2px rgba(0, 0, 0, 0.2)'
                  : '0 10px 15px -3px rgba(0, 0, 0, 0.08), 0 4px 6px -2px rgba(0, 0, 0, 0.04)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: isDark 
                    ? '0 20px 25px -5px rgba(0, 0, 0, 0.4), 0 10px 10px -5px rgba(0, 0, 0, 0.3)'
                    : '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0, flex: 1 }}>
                <Language sx={{ color: 'primary.main', mr: 2, flexShrink: 0 }} />
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  {editingField === 'website' ? (
                    <TextField
                      fullWidth
                      value={tempValue}
                      onChange={(e) => setTempValue(e.target.value)}
                      variant="standard"
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          handleSaveField();
                        }
                        if (e.key === 'Escape') {
                          e.preventDefault();
                          handleCancelEdit();
                        }
                      }}
                    />
                  ) : (
                    <>
                      <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
                        Website
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600, color: isDark ? 'white' : 'text.primary' }}>
                        {fields.website || "Not provided"}
                      </Typography>
                    </>
                  )}
                </Box>
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                {editingField === 'website' ? (
                  <>
                    <IconButton size="small" onClick={handleSaveField} color="primary">
                      <Save fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={handleCancelEdit}>
                      <Delete fontSize="small" />
                    </IconButton>
                  </>
                ) : (
                  <IconButton
                    size="small"
                    onClick={() => handleEditField('website', fields.website)}
                    sx={{ color: 'primary.main' }}
                  >
                    <Edit fontSize="small" />
                  </IconButton>
                )}
              </Box>
            </Paper>
          </Slide>

          {/* Address Card */}
          <Slide in timeout={1200} direction="up">
            <Paper
              elevation={0}
              sx={{
                display: 'flex',
                alignItems: 'center',
                p: 2.5,
                background: isDark 
                  ? 'linear-gradient(135deg, rgba(255, 255, 255, 0.06) 0%, rgba(255, 255, 255, 0.03) 100%)'
                  : 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(255, 255, 255, 0.7) 100%)',
                backdropFilter: 'blur(10px)',
                border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)'}`,
                borderRadius: 3,
                boxShadow: isDark 
                  ? '0 10px 15px -3px rgba(0, 0, 0, 0.3), 0 4px 6px -2px rgba(0, 0, 0, 0.2)'
                  : '0 10px 15px -3px rgba(0, 0, 0, 0.08), 0 4px 6px -2px rgba(0, 0, 0, 0.04)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: isDark 
                    ? '0 20px 25px -5px rgba(0, 0, 0, 0.4), 0 10px 10px -5px rgba(0, 0, 0, 0.3)'
                    : '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0, flex: 1 }}>
                <LocationOn sx={{ color: 'primary.main', mr: 2, flexShrink: 0 }} />
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  {editingField === 'address' ? (
                    <TextField
                      fullWidth
                      multiline
                      rows={2}
                      value={tempValue}
                      onChange={(e) => setTempValue(e.target.value)}
                      variant="standard"
                      autoFocus
                    />
                  ) : (
                    <>
                      <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
                        Address
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600, color: isDark ? 'white' : 'text.primary' }}>
                        {fields.address || "Not provided"}
                      </Typography>
                    </>
                  )}
                </Box>
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                {editingField === 'address' ? (
                  <>
                    <IconButton size="small" onClick={handleSaveField} color="primary">
                      <Save fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={handleCancelEdit}>
                      <Delete fontSize="small" />
                    </IconButton>
                  </>
                ) : (
                  <IconButton
                    size="small"
                    onClick={() => handleEditField('address', fields.address)}
                    sx={{ color: 'primary.main' }}
                  >
                    <Edit fontSize="small" />
                  </IconButton>
                )}
              </Box>
            </Paper>
          </Slide>

          {/* Group Selection Card */}
          <Slide in timeout={1300} direction="up">
            <Paper
              elevation={0}
              sx={{
                display: 'flex',
                alignItems: 'center',
                p: 2.5,
                background: isDark 
                  ? 'linear-gradient(135deg, rgba(255, 255, 255, 0.06) 0%, rgba(255, 255, 255, 0.03) 100%)'
                  : 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(255, 255, 255, 0.7) 100%)',
                backdropFilter: 'blur(10px)',
                border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)'}`,
                borderRadius: 3,
                boxShadow: isDark 
                  ? '0 10px 15px -3px rgba(0, 0, 0, 0.3), 0 4px 6px -2px rgba(0, 0, 0, 0.2)'
                  : '0 10px 15px -3px rgba(0, 0, 0, 0.08), 0 4px 6px -2px rgba(0, 0, 0, 0.04)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: isDark 
                    ? '0 20px 25px -5px rgba(0, 0, 0, 0.4), 0 10px 10px -5px rgba(0, 0, 0, 0.3)'
                    : '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0, flex: 1 }}>
                <Folder sx={{ color: 'primary.main', mr: 2, flexShrink: 0 }} />
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
                    Group
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600, color: isDark ? 'white' : 'text.primary' }}>
                    {selectedGroup?.name || "No group selected"}
                  </Typography>
                  {selectedGroup?.description && (
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      {selectedGroup.description}
                    </Typography>
                  )}
                </Box>
              </Box>
              <IconButton
                size="small"
                onClick={() => setGroupsDialogOpen(true)}
                sx={{ color: 'primary.main' }}
              >
                <Group fontSize="small" />
              </IconButton>
            </Paper>
          </Slide>

          {/* Tags Selection Card */}
          <Slide in timeout={1400} direction="up">
            <Paper
              elevation={0}
              sx={{
                display: 'flex',
                alignItems: 'center',
                p: 2.5,
                background: isDark 
                  ? 'linear-gradient(135deg, rgba(255, 255, 255, 0.06) 0%, rgba(255, 255, 255, 0.03) 100%)'
                  : 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(255, 255, 255, 0.7) 100%)',
                backdropFilter: 'blur(10px)',
                border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)'}`,
                borderRadius: 3,
                boxShadow: isDark 
                  ? '0 10px 15px -3px rgba(0, 0, 0, 0.3), 0 4px 6px -2px rgba(0, 0, 0, 0.2)'
                  : '0 10px 15px -3px rgba(0, 0, 0, 0.08), 0 4px 6px -2px rgba(0, 0, 0, 0.04)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: isDark 
                    ? '0 20px 25px -5px rgba(0, 0, 0, 0.4), 0 10px 10px -5px rgba(0, 0, 0, 0.3)'
                    : '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0, flex: 1 }}>
                <LocalOffer sx={{ color: 'primary.main', mr: 2, flexShrink: 0 }} />
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
                    Tags ({selectedTagsList.length})
                  </Typography>
                  {selectedTagsList.length > 0 ? (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                      {selectedTagsList.map((tag) => (
                        <Chip
                          key={tag.id}
                          label={tag.name}
                          size="small"
                          sx={{
                            backgroundColor: tag.color + '20',
                            color: tag.color,
                            fontSize: '0.75rem',
                            height: 20,
                          }}
                        />
                      ))}
                    </Box>
                  ) : (
                    <Typography variant="body1" sx={{ fontWeight: 600, color: isDark ? 'white' : 'text.primary' }}>
                      No tags selected
                    </Typography>
                  )}
                </Box>
              </Box>
              <IconButton
                size="small"
                onClick={() => setTagsDialogOpen(true)}
                sx={{ color: 'primary.main' }}
              >
                <Tag fontSize="small" />
              </IconButton>
            </Paper>
          </Slide>

          {/* Notes Card */}
          <Slide in timeout={1500} direction="up">
            <Paper
              elevation={0}
              sx={{
                display: 'flex',
                alignItems: 'center',
                p: 2.5,
                background: isDark 
                  ? 'linear-gradient(135deg, rgba(255, 255, 255, 0.06) 0%, rgba(255, 255, 255, 0.03) 100%)'
                  : 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(255, 255, 255, 0.7) 100%)',
                backdropFilter: 'blur(10px)',
                border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)'}`,
                borderRadius: 3,
                boxShadow: isDark 
                  ? '0 10px 15px -3px rgba(0, 0, 0, 0.3), 0 4px 6px -2px rgba(0, 0, 0, 0.2)'
                  : '0 10px 15px -3px rgba(0, 0, 0, 0.08), 0 4px 6px -2px rgba(0, 0, 0, 0.04)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: isDark 
                    ? '0 20px 25px -5px rgba(0, 0, 0, 0.4), 0 10px 10px -5px rgba(0, 0, 0, 0.3)'
                    : '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0, flex: 1 }}>
                <Notes sx={{ color: 'primary.main', mr: 2, flexShrink: 0 }} />
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  {editingField === 'notes' ? (
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      value={tempValue}
                      onChange={(e) => setTempValue(e.target.value)}
                      variant="standard"
                      autoFocus
                    />
                  ) : (
                    <>
                      <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
                        Notes
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600, color: isDark ? 'white' : 'text.primary' }}>
                        {fields.notes || "No notes added"}
                      </Typography>
                    </>
                  )}
                </Box>
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                {editingField === 'notes' ? (
                  <>
                    <IconButton size="small" onClick={handleSaveField} color="primary">
                      <Save fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={handleCancelEdit}>
                      <Delete fontSize="small" />
                    </IconButton>
                  </>
                ) : (
                  <IconButton
                    size="small"
                    onClick={() => handleEditField('notes', fields.notes)}
                    sx={{ color: 'primary.main' }}
                  >
                    <Edit fontSize="small" />
                  </IconButton>
                )}
              </Box>
            </Paper>
          </Slide>
        </Stack>
      </Box>

      {/* Floating Action Buttons */}
      <Box
        sx={{
          position: 'fixed',
          bottom: 20,
          right: 20,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <Fab
          color="primary"
          onClick={handleSaveContact}
          disabled={loading}
          sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
              transform: 'scale(1.1)',
            },
            transition: 'all 0.2s ease',
          }}
        >
          {loading ? <div>...</div> : <Save />}
        </Fab>
        <Fab
          onClick={handleShare}
          sx={{
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)',
            color: 'primary.main',
            '&:hover': {
              background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.2) 0%, rgba(255, 255, 255, 0.1) 100%)',
              transform: 'scale(1.1)',
            },
            transition: 'all 0.2s ease',
          }}
        >
          <Share />
        </Fab>
      </Box>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%', borderRadius: '12px' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Groups Manager Dialog */}
      <GroupsManager
        open={groupsDialogOpen}
        onClose={() => setGroupsDialogOpen(false)}
        onGroupSelect={handleGroupSelect}
        selectedGroup={selectedGroup}
      />

      {/* Tags Manager Dialog */}
      <TagsManager
        open={tagsDialogOpen}
        onClose={() => setTagsDialogOpen(false)}
        onTagSelect={handleTagSelect}
        selectedTags={selectedTagsList}
      />

      {/* Duplicate Phone Number Alert Dialog */}
      <Dialog
        open={showDuplicateAlert}
        onClose={() => setShowDuplicateAlert(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 4,
            background: isDark 
              ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.95) 0%, rgba(15, 23, 42, 0.9) 100%)'
              : 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.9) 100%)',
            backdropFilter: 'blur(20px)',
            border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          color: isDark ? 'white' : 'text.primary',
          pb: 2 
        }}>
          <Warning sx={{ color: 'warning.main', mr: 2 }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Duplicate Contact Detected
          </Typography>
          <IconButton
            onClick={() => setShowDuplicateAlert(false)}
            sx={{ ml: 'auto', color: isDark ? 'white' : 'text.secondary' }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ pt: 0 }}>
          <Typography 
            variant="body1" 
            sx={{ 
              color: isDark ? 'rgba(203, 213, 225, 0.9)' : 'text.secondary',
              mb: 3,
              lineHeight: 1.6
            }}
          >
            The phone number(s) from this business card appear to already exist in your contacts. 
            Review the existing contacts below:
          </Typography>
          
          <List sx={{ 
            maxHeight: 300, 
            overflow: 'auto',
            bgcolor: isDark 
              ? 'rgba(255, 255, 255, 0.02)' 
              : 'rgba(0, 0, 0, 0.02)',
            borderRadius: 2,
            p: 1
          }}>
            {duplicateContacts.map((duplicate, index) => (
              <ListItem
                key={`${duplicate.contactId}-${index}`}
                sx={{
                  mb: 1,
                  borderRadius: 2,
                  background: isDark 
                    ? 'linear-gradient(135deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.02) 100%)'
                    : 'linear-gradient(135deg, rgba(255, 255, 255, 0.8) 0%, rgba(255, 255, 255, 0.6) 100%)',
                  border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)'}`,
                  '&:hover': {
                    background: isDark 
                      ? 'linear-gradient(135deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.04) 100%)'
                      : 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(255, 255, 255, 0.7) 100%)',
                  }
                }}
              >
                <ListItemIcon>
                  <Person sx={{ color: 'primary.main' }} />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography 
                      variant="subtitle1" 
                      sx={{ 
                        fontWeight: 600,
                        color: isDark ? 'white' : 'text.primary'
                      }}
                    >
                      {duplicate.contactName}
                    </Typography>
                  }
                  secondary={
                    <Box>
                      {duplicate.contactDesignation && (
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            color: isDark ? 'rgba(203, 213, 225, 0.7)' : 'text.secondary',
                            fontSize: '0.85rem'
                          }}
                        >
                          {duplicate.contactDesignation}
                          {duplicate.contactOrg && ` at ${duplicate.contactOrg}`}
                        </Typography>
                      )}
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: 'warning.main',
                          fontWeight: 500,
                          mt: 0.5,
                          fontSize: '0.85rem'
                        }}
                      >
                        📞 {duplicate.existingPhone}
                      </Typography>
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          color: isDark ? 'rgba(203, 213, 225, 0.6)' : 'text.secondary',
                          fontSize: '0.75rem'
                        }}
                      >
                        Matches scanned: {duplicate.scannedPhone}
                      </Typography>
                    </Box>
                  }
                />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        
        <DialogActions sx={{ p: 3, pt: 2 }}>
          <Button
            onClick={() => setShowDuplicateAlert(false)}
            variant="outlined"
            sx={{
              color: isDark ? 'white' : 'primary.main',
              borderColor: isDark ? 'rgba(255, 255, 255, 0.3)' : 'primary.main',
              '&:hover': {
                borderColor: isDark ? 'rgba(255, 255, 255, 0.5)' : 'primary.dark',
                background: isDark 
                  ? 'rgba(255, 255, 255, 0.05)' 
                  : 'rgba(25, 118, 210, 0.04)',
              }
            }}
          >
            Continue Anyway
          </Button>
          <Button
            onClick={() => {
              setShowDuplicateAlert(false);
              navigate('/dashboard');
            }}
            variant="contained"
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
              }
            }}
          >
            Go to Dashboard
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Information;